import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useCurrency } from '../../contexts/CompanyContext';
import { toast } from 'sonner';
import {
  ChartLine,
  TrendUp,
  TrendDown,
  ArrowRight,
  Gauge,
  Calendar,
  Info
} from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  AreaChart,
  Area,
  ComposedChart,
  Bar
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ForecastingPage() {
  const { token } = useAuth();
  const { formatCurrency } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [forecastData, setForecastData] = useState(null);
  const [months, setMonths] = useState('6');

  const fetchForecast = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API}/api/analytics/forecast?months=${months}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        setForecastData(await res.json());
      }
    } catch (err) {
      toast.error('Failed to load forecast data');
    } finally {
      setLoading(false);
    }
  }, [token, months]);

  useEffect(() => {
    fetchForecast();
  }, [fetchForecast]);

  const getConfidenceBadge = (confidence) => {
    const styles = {
      high: 'bg-emerald-500/10 text-emerald-600',
      medium: 'bg-amber-500/10 text-amber-600',
      low: 'bg-red-500/10 text-red-600'
    };
    return <Badge className={styles[confidence] || styles.low}>{confidence} confidence</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="forecast-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const historical = forecastData?.historical || [];
  const forecasts = forecastData?.forecasts || [];
  const methodology = forecastData?.methodology || {};
  const metrics = forecastData?.metrics || {};
  const yoyComparison = forecastData?.yoy_comparison;

  // Combine historical and forecast data for chart
  const combinedData = [
    ...historical.map(h => ({
      ...h,
      type: 'actual',
      revenue_actual: h.revenue,
      expense_actual: h.expense
    })),
    ...forecasts.map(f => ({
      period: f.period,
      revenue_forecast: f.revenue_forecast,
      expense_forecast: f.expense_forecast,
      cash_flow_forecast: f.cash_flow_forecast,
      type: 'forecast'
    }))
  ];

  // Calculate forecast totals
  const totalForecastRevenue = forecasts.reduce((sum, f) => sum + f.revenue_forecast, 0);
  const totalForecastExpense = forecasts.reduce((sum, f) => sum + f.expense_forecast, 0);
  const totalForecastProfit = totalForecastRevenue - totalForecastExpense;

  return (
    <div className="space-y-8" data-testid="forecasting-page">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-semibold">
            Financial Forecasting
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base leading-relaxed mt-1">
            Revenue, expense, and cash flow projections based on historical trends
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <Select value={months} onValueChange={setMonths}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Forecast period" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="3">Next 3 months</SelectItem>
              <SelectItem value="6">Next 6 months</SelectItem>
              <SelectItem value="12">Next 12 months</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={fetchForecast} variant="outline">Refresh</Button>
        </div>
      </motion.div>

      {/* Methodology Info */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="card-tactile rounded-lg p-4 border-l-4 border-l-blue-500"
      >
        <div className="flex items-start gap-3">
          <Info size={20} className="text-blue-500 mt-0.5" weight="fill" />
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <span className="font-medium">Forecast Methodology</span>
              {getConfidenceBadge(methodology.confidence)}
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {methodology.type} • Based on {methodology.periods_analyzed} months of historical data
            </p>
          </div>
        </div>
      </motion.div>

      {/* Forecast Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="card-tactile rounded-lg p-6"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-lg bg-emerald-500/10">
              <TrendUp size={20} className="text-emerald-600" weight="duotone" />
            </div>
            <span className="text-xs tracking-wider uppercase font-semibold text-muted-foreground">
              Forecasted Revenue
            </span>
          </div>
          <p className="text-2xl font-heading font-semibold text-emerald-600">
            {formatCurrency(totalForecastRevenue)}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Avg: {formatCurrency(metrics.avg_monthly_revenue)}/mo • Trend: {metrics.revenue_trend}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card-tactile rounded-lg p-6"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-lg bg-amber-500/10">
              <TrendDown size={20} className="text-amber-600" weight="duotone" />
            </div>
            <span className="text-xs tracking-wider uppercase font-semibold text-muted-foreground">
              Forecasted Expenses
            </span>
          </div>
          <p className="text-2xl font-heading font-semibold text-amber-600">
            {formatCurrency(totalForecastExpense)}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Avg: {formatCurrency(metrics.avg_monthly_expense)}/mo • Trend: {metrics.expense_trend}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="card-tactile rounded-lg p-6"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className={`p-2 rounded-lg ${totalForecastProfit >= 0 ? 'bg-blue-500/10' : 'bg-red-500/10'}`}>
              <ChartLine size={20} className={totalForecastProfit >= 0 ? 'text-blue-600' : 'text-red-600'} weight="duotone" />
            </div>
            <span className="text-xs tracking-wider uppercase font-semibold text-muted-foreground">
              Projected Profit
            </span>
          </div>
          <p className={`text-2xl font-heading font-semibold ${totalForecastProfit >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
            {formatCurrency(totalForecastProfit)}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Over next {months} months
          </p>
        </motion.div>
      </div>

      {/* YoY Comparison (if available) */}
      {yoyComparison && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="card-tactile rounded-lg p-6"
        >
          <h3 className="font-heading text-lg font-medium mb-4">Year-over-Year Comparison</h3>
          <div className="flex items-center gap-8">
            <div>
              <p className="text-sm text-muted-foreground">Current Period</p>
              <p className="text-xl font-semibold">{formatCurrency(yoyComparison.current_period)}</p>
            </div>
            <ArrowRight size={24} className="text-muted-foreground" />
            <div>
              <p className="text-sm text-muted-foreground">Previous Period</p>
              <p className="text-xl font-semibold">{formatCurrency(yoyComparison.previous_period)}</p>
            </div>
            <div className={`px-4 py-2 rounded-lg ${yoyComparison.revenue_change >= 0 ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}>
              <p className={`text-lg font-bold ${yoyComparison.revenue_change >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {yoyComparison.revenue_change >= 0 ? '+' : ''}{yoyComparison.revenue_change}%
              </p>
              <p className="text-xs text-muted-foreground">Change</p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Main Forecast Chart */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35 }}
        className="card-tactile rounded-lg p-6"
      >
        <h3 className="font-heading text-lg font-medium mb-4">Actual vs Forecast</h3>
        <div className="h-[400px]">
          {combinedData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={combinedData}>
                <defs>
                  <linearGradient id="forecastRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.2}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="period" className="text-xs" />
                <YAxis className="text-xs" tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))', 
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                  formatter={(value, name) => [formatCurrency(value), name]}
                />
                <Legend />
                {/* Actual data - solid lines */}
                <Line 
                  type="monotone" 
                  dataKey="revenue_actual" 
                  name="Revenue (Actual)" 
                  stroke="#10b981" 
                  strokeWidth={2}
                  dot={{ fill: '#10b981', r: 4 }}
                  connectNulls
                />
                <Line 
                  type="monotone" 
                  dataKey="expense_actual" 
                  name="Expense (Actual)" 
                  stroke="#f59e0b" 
                  strokeWidth={2}
                  dot={{ fill: '#f59e0b', r: 4 }}
                  connectNulls
                />
                {/* Forecast data - dashed lines */}
                <Line 
                  type="monotone" 
                  dataKey="revenue_forecast" 
                  name="Revenue (Forecast)" 
                  stroke="#10b981" 
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={{ fill: '#10b981', r: 4 }}
                  connectNulls
                />
                <Line 
                  type="monotone" 
                  dataKey="expense_forecast" 
                  name="Expense (Forecast)" 
                  stroke="#f59e0b" 
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={{ fill: '#f59e0b', r: 4 }}
                  connectNulls
                />
              </ComposedChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-muted-foreground">
              <div className="text-center">
                <ChartLine size={48} weight="duotone" className="mx-auto mb-2 opacity-50" />
                <p>Not enough data for forecasting</p>
                <p className="text-sm">Need at least 3 months of historical data</p>
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* Cash Flow Forecast */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="card-tactile rounded-lg p-6"
      >
        <h3 className="font-heading text-lg font-medium mb-4">Cash Flow Projection</h3>
        <div className="h-[300px]">
          {forecasts.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={forecasts}>
                <defs>
                  <linearGradient id="cashFlowGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.4}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="period" className="text-xs" />
                <YAxis className="text-xs" tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))', 
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                  formatter={(value) => [formatCurrency(value), 'Cash Flow']}
                />
                <ReferenceLine y={0} stroke="#ef4444" strokeDasharray="3 3" />
                <Area 
                  type="monotone" 
                  dataKey="cash_flow_forecast" 
                  name="Cash Flow" 
                  stroke="#3b82f6" 
                  fillOpacity={1} 
                  fill="url(#cashFlowGradient)" 
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-muted-foreground">
              <p>No cash flow forecast available</p>
            </div>
          )}
        </div>
      </motion.div>

      {/* Forecast Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.45 }}
        className="card-tactile rounded-lg p-6"
      >
        <h3 className="font-heading text-lg font-medium mb-4">Monthly Forecast Breakdown</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-3 px-4">Period</th>
                <th className="text-right py-3 px-4">Revenue</th>
                <th className="text-right py-3 px-4">Expenses</th>
                <th className="text-right py-3 px-4">Cash Flow</th>
                <th className="text-center py-3 px-4">Confidence</th>
              </tr>
            </thead>
            <tbody>
              {forecasts.map((f, idx) => (
                <tr key={idx} className="border-b hover:bg-muted/30">
                  <td className="py-3 px-4 font-medium">{f.period}</td>
                  <td className="py-3 px-4 text-right text-emerald-600">{formatCurrency(f.revenue_forecast)}</td>
                  <td className="py-3 px-4 text-right text-amber-600">{formatCurrency(f.expense_forecast)}</td>
                  <td className={`py-3 px-4 text-right ${f.cash_flow_forecast >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                    {formatCurrency(f.cash_flow_forecast)}
                  </td>
                  <td className="py-3 px-4 text-center">
                    {getConfidenceBadge(f.confidence)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
}
